/**
 * Listing Detail Page - Public view of a marketplace listing
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  MapPin,
  MessageCircle,
  Tag,
  Clock,
  User,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { api } from '../../services/api';
import {
  PhotoGallery,
  ConditionBadge,
  AssemblyStatusBadge,
  SellerRating,
} from '../../components/marketplace';
import { useAuthStore } from '../../stores/auth-store';
import { useLanguage } from '../../contexts/LanguageContext';

export default function ListingDetail() {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [message, setMessage] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => api.getMarketplaceListing(id!),
    enabled: !!id,
  });

  const contactMutation = useMutation({
    mutationFn: ({ listingId, initialMessage }: { listingId: string; initialMessage: string }) =>
      api.startMarketplaceConversation(listingId, initialMessage),
    onSuccess: (result) => {
      if (result.data?.conversation_id) {
        navigate(`/account/marketplace/messages/${result.data.conversation_id}`);
      }
    },
  });

  const handleContact = () => {
    if (!user) {
      navigate('/auth/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    if (!message.trim()) {
      return;
    }

    contactMutation.mutate({ listingId: id!, initialMessage: message });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('listingDetail.notFound.title', 'Listing Not Found')}</h2>
        <p className="text-gray-600 mb-6">
          {t('listingDetail.notFound.description', 'This listing may have been removed or sold.')}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('listingDetail.goBack', 'Go Back')}
        </button>
      </div>
    );
  }

  const listing = data.data;
  const discountPercent = listing.retail_price
    ? Math.round((1 - listing.asking_price / listing.retail_price) * 100)
    : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('listingDetail.back', 'Back')}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Photos */}
        <div>
          <PhotoGallery photos={listing.photos || []} productCode={listing.product_code} />
        </div>

        {/* Details */}
        <div className="space-y-6">
          {/* Price & Status */}
          <div>
            {listing.status !== 'active' && (
              <div className="mb-2">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  listing.status === 'sold'
                    ? 'bg-gray-100 text-gray-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {listing.status === 'sold' ? t('listingDetail.status.sold', 'Sold') : t('listingDetail.status.unavailable', 'No Longer Available')}
                </span>
              </div>
            )}

            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-3xl font-bold text-gray-900">
                {formatPrice(listing.asking_price)}
              </span>
              {listing.retail_price && listing.retail_price > listing.asking_price && (
                <span className="text-lg text-gray-400 line-through">
                  {formatPrice(listing.retail_price)}
                </span>
              )}
              {discountPercent && discountPercent > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                  <Tag className="w-3.5 h-3.5" />
                  {t('listingDetail.discount', '{{percent}}% OFF', { percent: discountPercent })}
                </span>
              )}
            </div>
          </div>

          {/* Product Code */}
          <div>
            <a
              href={`https://www.ikea.com/us/en/search/?q=${listing.product_code.replace(/\./g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
            >
              {listing.product_code}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Condition & Assembly */}
          <div className="flex flex-wrap gap-3">
            <ConditionBadge condition={listing.condition} size="md" />
            <AssemblyStatusBadge status={listing.assembly_status} size="md" />
          </div>

          {/* Missing Parts / Modifications */}
          {(listing.missing_parts || listing.modifications) && (
            <div className="space-y-2">
              {listing.missing_parts && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('listingDetail.missingParts', 'Missing parts')}:</span> {listing.missing_parts}
                </p>
              )}
              {listing.modifications && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('listingDetail.modifications', 'Modifications')}:</span> {listing.modifications}
                </p>
              )}
            </div>
          )}

          {/* Description */}
          {listing.description && (
            <div>
              <h3 className="font-medium text-gray-900 mb-2">{t('listingDetail.description', 'Description')}</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{listing.description}</p>
            </div>
          )}

          {/* Location */}
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className="w-5 h-5 text-gray-400" />
            <span>
              {listing.neighborhood || listing.postal_code}
              {listing.distance_km !== null && listing.distance_km !== undefined && (
                <span className="ml-1 text-gray-400">
                  {t('listingDetail.distanceAway', '({{distance}} km away)', { distance: listing.distance_km.toFixed(1) })}
                </span>
              )}
            </span>
          </div>

          {/* Posted Date */}
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Clock className="w-4 h-4" />
            <span>{t('listingDetail.listedOn', 'Listed {{date}}', { date: formatDate(listing.created_at) })}</span>
          </div>

          {/* Seller Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{t('listingDetail.seller', 'Seller')}</p>
                <SellerRating
                  rating={listing.seller_stats?.seller_avg_overall ?? null}
                  count={listing.seller_stats?.seller_rating_count || 0}
                />
                {listing.seller_stats && listing.seller_stats.total_sales > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {listing.seller_stats.total_sales === 1
                      ? t('listingDetail.completedSale', '1 completed sale')
                      : t('listingDetail.completedSales', '{{count}} completed sales', { count: listing.seller_stats.total_sales })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Button */}
          {listing.status === 'active' && !listing.is_own_listing && (
            <div>
              {!showContactForm ? (
                <button
                  onClick={() => setShowContactForm(true)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t('listingDetail.contactSeller', 'Contact Seller')}
                </button>
              ) : (
                <div className="space-y-3">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('listingDetail.messagePlaceholder', 'Hi! Is this item still available?')}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional UX: focus on message field when contact form opens
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowContactForm(false)}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t('listingDetail.cancel', 'Cancel')}
                    </button>
                    <button
                      onClick={handleContact}
                      disabled={!message.trim() || contactMutation.isPending}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {contactMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <MessageCircle className="w-4 h-4" />
                          {t('listingDetail.sendMessage', 'Send Message')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {contactMutation.error && (
                <p className="mt-2 text-sm text-red-600">
                  {t('listingDetail.sendError', 'Failed to send message. Please try again.')}
                </p>
              )}
            </div>
          )}

          {/* Own Listing Actions */}
          {listing.is_own_listing && (
            <div className="flex gap-3">
              <Link
                to={`/account/marketplace/edit/${listing.id}`}
                className="flex-1 px-4 py-2 text-center text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('listingDetail.editListing', 'Edit Listing')}
              </Link>
              <Link
                to="/account/marketplace"
                className="flex-1 px-4 py-2 text-center text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                {t('listingDetail.viewAllListings', 'View All Listings')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
